import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { useEmailDomains, useCreateEmailDomain } from '@/hooks/useEmailSettings';
import { Mail, Plus, Shield } from 'lucide-react';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import { StatusBadge } from '@/components/status/StatusBadge';

const EmailSettingsAdmin = () => {
  const { data: domains, isLoading: domainsLoading } = useEmailDomains();
  const createDomain = useCreateEmailDomain();
  
  const [newDomain, setNewDomain] = useState('');

  const handleAddDomain = (e: React.FormEvent) => {
    e.preventDefault();
    if (newDomain.trim()) {
      createDomain.mutate(newDomain.trim());
      setNewDomain('');
    }
  };

  if (domainsLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <LoadingSpinner size="medium" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Domain Management */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Shield className="h-5 w-5" />
            Email Domain Verification
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-6">
            {/* Add New Domain */}
            <form onSubmit={handleAddDomain} className="flex gap-3">
              <div className="flex-1">
                <Label htmlFor="domain" className="sr-only">Domain</Label>
                <Input
                  id="domain"
                  value={newDomain}
                  onChange={(e) => setNewDomain(e.target.value)}
                  placeholder="yourdomain.com"
                />
              </div>
              <Button 
                type="submit" 
                disabled={createDomain.isPending || !newDomain.trim()}
                className="bg-purple-600 hover:bg-purple-700 text-white"
              >
                {createDomain.isPending ? (
                  <LoadingSpinner size="small" iconClassName="text-white" />
                ) : (
                  <>
                    <Plus className="h-4 w-4 mr-2" />
                    Add Domain
                  </>
                )}
              </Button>
            </form>

            {/* Existing Domains */}
            {domains && domains.length > 0 && (
              <div className="space-y-3">
                <h4 className="font-medium">Configured Domains</h4>
                {domains.map((domain) => (
                  <div key={domain.id} className="flex items-center justify-between p-4 border rounded-lg">
                    <div className="flex items-center gap-3">
                      <Mail className="h-4 w-4 text-gray-500" />
                      <span className="font-medium">{domain.name}</span>
                      <StatusBadge status={domain.status} />
                    </div>
                    <div className="text-sm text-gray-500">
                      Added {new Date(domain.created_at).toLocaleDateString()}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {domains && domains.length === 0 && (
              <div className="text-center py-8 text-gray-500">
                <Mail className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                <p>No domains configured yet</p>
                <p className="text-sm">Add a domain above to get started with email sending</p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default EmailSettingsAdmin;
